import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Users, Route as RouteIcon, Mountain, Navigation } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];
const PINS = [
  { id: "p1", x: 18, y: 28, live: true },
  { id: "p2", x: 38, y: 56, live: false },
  { id: "p3", x: 62, y: 36, live: true },
  { id: "p4", x: 78, y: 64, live: false },
  { id: "p5", x: 52, y: 22, live: true },
];
const RIDERS = [
  { id: "r1", x: 28, y: 42 }, { id: "r2", x: 46, y: 30 },
  { id: "r3", x: 68, y: 50 }, { id: "r4", x: 84, y: 38 },
];

export const InteractiveMap = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState("routes");
  const [hover, setHover] = useState(null);
  const TABS = [
    { id: "routes", label: t.map.tabRoutes, icon: RouteIcon },
    { id: "live", label: t.map.tabLive, icon: Users },
    { id: "pins", label: t.map.tabPins, icon: MapPin },
  ];

  return (
    <section id="map" data-testid="interactive-map-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 map-ambient pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 14, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.6, ease }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.map.eyebrow}</span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">{t.map.title1}<br />{t.map.title2}</h2>
            <p className="mt-4 text-white/65 text-base leading-relaxed">{t.map.subhead}</p>
          </div>
          <div data-testid="interactive-map-tabs" className="inline-flex items-center gap-1 rounded-2xl border border-white/10 bg-black/30 backdrop-blur-xl p-1" role="tablist">
            {TABS.map((tb) => {
              const Icon = tb.icon;
              const active = tab === tb.id;
              return (
                <button key={tb.id} role="tab" aria-selected={active} data-testid={`map-tab-${tb.id}`} onClick={() => setTab(tb.id)}
                  className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium transition-colors duration-200 ${
                    active ? "bg-[#39FF14] text-black shadow-[0_0_0_1px_rgba(57,255,20,0.35),0_0_24px_rgba(57,255,20,0.20)]" : "text-white/75 hover:text-white hover:bg-white/5"
                  }`}>
                  <Icon className="w-3.5 h-3.5" />{tb.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 24, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, ease, delay: 0.1 }}
          className="relative mt-10 rounded-[28px] border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
          data-testid="interactive-map-canvas"
        >
          <div className="relative aspect-[16/10] grid-bg">
            <div className="absolute -left-32 -top-32 w-[480px] h-[480px] rounded-full bg-[#39FF14]/10 blur-3xl" />
            <div className="absolute -right-24 -bottom-24 w-[420px] h-[420px] rounded-full bg-[#7DD3FC]/8 blur-3xl" />
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 62" preserveAspectRatio="none">
              <path d="M 5 50 C 20 40, 30 55, 50 45 S 80 30, 95 36" stroke="rgba(255,255,255,0.10)" strokeWidth="0.5" fill="none" />
              <path d="M 8 18 C 25 22, 40 12, 55 22 S 85 22, 96 14" stroke="rgba(255,255,255,0.10)" strokeWidth="0.5" fill="none" />
              <path d="M 6 34 C 18 26, 28 42, 44 32 C 58 22, 72 36, 90 30" stroke="rgba(57,255,20,0.85)" strokeWidth="0.7" fill="none" strokeLinecap="round" strokeDasharray="4 3" className="route-dash" style={{ filter: "drop-shadow(0 0 4px rgba(57,255,20,0.65))" }} />
              <path d="M 10 12 C 30 30, 55 8, 70 26 C 80 38, 92 30, 96 44" stroke="rgba(125,211,252,0.55)" strokeWidth="0.5" fill="none" strokeDasharray="3 4" className="route-dash" />
            </svg>

            {(tab === "pins" || tab === "routes") && PINS.map((p) => (
              <button key={p.id} data-testid={`map-pin-${p.id}`}
                onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(p)} onBlur={() => setHover(null)}
                className="absolute -translate-x-1/2 -translate-y-1/2 outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70 rounded-full"
                style={{ left: `${p.x}%`, top: `${p.y}%` }} aria-label={t.map.pins[p.id]}>
                <span className="relative inline-flex w-4 h-4">
                  {p.live && <span className="absolute inset-0 rounded-full pulse-ring" style={{ background: "rgba(57,255,20,0.6)" }} />}
                  <span className="relative inline-block w-4 h-4 rounded-full bg-black border-2 border-[#39FF14] shadow-[0_0_18px_rgba(57,255,20,0.55)]">
                    <span className="absolute inset-[3px] rounded-full bg-[#39FF14]" />
                  </span>
                </span>
              </button>
            ))}

            {(tab === "live" || tab === "routes") && RIDERS.map((r) => (
              <span key={r.id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${r.x}%`, top: `${r.y}%` }} aria-hidden="true">
                <span className="relative inline-flex w-3 h-3">
                  <span className="absolute inset-0 rounded-full pulse-ring" style={{ background: "rgba(125,211,252,0.7)" }} />
                  <span className="relative inline-block w-3 h-3 rounded-full bg-[#7DD3FC] shadow-[0_0_14px_rgba(125,211,252,0.65)]" />
                </span>
              </span>
            ))}

            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-black/45 backdrop-blur-xl px-3.5 py-2.5">
              <Mountain className="w-4 h-4 text-[#39FF14]" />
              <div className="flex flex-col leading-tight">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/55">{t.map.regionLabel}</span>
                <span className="font-display font-semibold text-white text-sm">{t.map.regionName}</span>
              </div>
            </div>

            <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 grid grid-cols-3 gap-2 sm:gap-3">
              <div className="rounded-xl border border-white/10 bg-black/45 backdrop-blur-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/55">{t.map.distance}</div>
                <div className="font-display font-semibold text-white text-sm">312 km</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/45 backdrop-blur-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/55">{t.map.ridersLive}</div>
                <div className="font-display font-semibold text-[#39FF14] text-sm">{t.map.ridersValue}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/45 backdrop-blur-xl px-3 py-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/55">{t.map.pace}</div>
                <div className="font-display font-semibold text-white text-sm">78 km/h</div>
              </div>
            </div>

            {hover && (
              <div className="absolute z-10 -translate-x-1/2 -translate-y-[140%] inline-flex items-center gap-2 rounded-xl border border-white/12 bg-black/70 backdrop-blur-xl px-3 py-1.5 shadow-[0_18px_60px_rgba(0,0,0,0.45)]" style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
                <Navigation className="w-3 h-3 text-[#39FF14]" />
                <span className="text-xs text-white font-medium">{t.map.pins[hover.id]}</span>
                {hover.live && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#39FF14]">{t.map.live}</span>}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-t border-white/8 bg-black/30">
            <div className="flex items-center gap-2 text-white/60 text-xs font-mono uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_10px_rgba(57,255,20,0.7)]" />
              {t.map.liveUpdated}
            </div>
            <button data-testid="map-start-live-ride-button" onClick={() => document.querySelector("#download")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 rounded-xl bg-[#39FF14] text-black font-semibold px-4 py-2 text-sm hover:bg-[#6BFF55] transition-colors">
              <Navigation className="w-4 h-4" />{t.map.startRide}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
